"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import IconButton from "~/components/shared/IconButton";
import { IoMenu, IoSearch } from "react-icons/io5";
import { Link } from "~/i18n/navigation";
import { cn } from "~/lib/utils";

import dynamic from "next/dynamic";
const SearchModal = dynamic(
  () => import("./SearchModal").then((mod) => mod.SearchModal),
  { ssr: false },
);

import { UserButton } from "~/components/auth/user-button";
import { useTranslations } from "next-intl";
import { LanguageSwitcher } from "~/components/layout/LanguageSwitcher";

interface NavProps {
  hideLinks?: boolean;
}

const Nav = ({ hideLinks = false }: NavProps = {}) => {
  const t = useTranslations("Nav");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const reopen = sessionStorage.getItem("porypro_reopen_search") === "true";
        if (reopen) {
          sessionStorage.removeItem("porypro_reopen_search");
          return true;
        }
      } catch {}
    }
    return false;
  });

  useEffect(() => {
    try {
      if (sessionStorage.getItem("porypro_reopen_search") === "true") {
        sessionStorage.removeItem("porypro_reopen_search");
        setIsSearchOpen(true);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  function handleSearchOpen() {
    setIsSearchOpen(true);
  }

  return (
    <>
      <div className="relative z-40 flex justify-center bg-white px-2.5 py-1 shadow-lg sm:px-6">
        <SearchModal isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />

        <div className="relative flex w-full max-w-[1440px] items-center gap-1.5 sm:gap-4 text-neutral-900">
          <div className="flex md:hidden">
            <IconButton
              onClick={() => setIsMenuOpen((prev) => !prev)}
              styleType="ghost"
              ariaLabel={t("openMenuAria")}
            >
              <IoMenu className="h-6 w-6" />
            </IconButton>
          </div>

          <Link
            href="/"
            className="h-[36px] w-[115px] shrink-0 sm:h-[40px] sm:w-[140px] md:h-[50px] md:w-[200px]"
          >
            {/* Responsive image sizing handled by container or object-contain */}
            <Image
              className="h-full w-full object-contain md:object-left"
              src="/porypro-logotype-clearspace.svg"
              alt="PoryPro"
              width={200}
              height={50}
            />
          </Link>

          {!hideLinks && (
            <ul className="[&>li:hover]:text-pory-pink-400 mr-auto hidden items-center gap-4 font-bold md:flex [&>li:hover]:underline">
              <li>
                <Link href="/pokemon">{t("pokemon")}</Link>
              </li>
              <li>
                <Link href="/nests">{t("nests")}</Link>
              </li>
              <li>
                <Link href="/invasion">{t("invasion")}</Link>
              </li>
              <li>
                <Link href="/quests">{t("quests")}</Link>
              </li>
              <li>
                <Link href="/raids">{t("raids")}</Link>
              </li>
              <li>
                <Link href="/map">{t("map")}</Link>
              </li>
              <li>
                <Link href="/guide">{t("guide")}</Link>
              </li>
            </ul>
          )}

          <div className="flex flex-1 items-center justify-end gap-1 sm:gap-2 md:ml-auto md:flex-none">
            {/* Search Button Mobile */}
            <IconButton
              onClick={handleSearchOpen}
              className="flex lg:hidden"
              styleType="ghost"
              ariaLabel={t("searchAria")}
            >
              <IoSearch />
            </IconButton>

            {/* Search Bar Desktop */}
            <button
              onClick={handleSearchOpen}
              className="border-pory-pink-400 mr-2 hidden min-h-9 w-60 cursor-text rounded-sm border-2 bg-white pl-2 text-left text-base lg:block"
            >
              {t("searchPlaceholder")}
            </button>
            <LanguageSwitcher variant="nav" className="mr-1 hidden sm:inline-flex" />
            <UserButton />
          </div>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      <div
        className={cn(
          "fixed inset-0 z-50 bg-black/20 transition-opacity duration-200 md:hidden",
          isMenuOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0",
        )}
        onClick={() => setIsMenuOpen(false)}
      >
        <div
          className={cn(
            "absolute top-0 left-0 z-50 flex h-full w-[60%] flex-col items-start gap-4 bg-white p-6 transition-transform duration-200",
            isMenuOpen ? "translate-x-0" : "-translate-x-full",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {!hideLinks && (
            <ul className="flex flex-col items-start gap-4 w-full">
              <li>
                <Link href="/pokemon">{t("pokemon")}</Link>
              </li>
              <li>
                <Link href="/nests">{t("nests")}</Link>
              </li>
              <li>
                <Link href="/invasion">{t("invasion")}</Link>
              </li>
              <li>
                <Link href="/quests">{t("quests")}</Link>
              </li>
              <li>
                <Link href="/raids">{t("raids")}</Link>
              </li>
              <li>
                <Link href="/map">{t("map")}</Link>
              </li>
              <li>
                <Link href="/guide">{t("guide")}</Link>
              </li>
            </ul>
          )}
          <div className="mt-auto w-full pt-4 border-t border-slate-100">
            <LanguageSwitcher variant="mobile" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Nav;
